import { useMemo, useState } from 'react';
import { NormalizedTask } from '../../domain/normalize';
import { UserDoc, PROYECTO_STATUSES, PROYECTO_STATUS_LABELS, PROYECTO_STATUS_COLORS } from '../../domain/types';
import { nameByEmail, fmtDate, fmtDia } from '../../domain/util';
import { ESTADO_ENTREGA_LABELS, ESTADO_ENTREGA_COLORS } from '../../domain/config';
import { Card, Kpi, ExportButton, BoardIntro } from './shared';

type SortKey = 'deadline' | 'dias_activo' | 'responsable' | 'urgencia' | 'estado';

const URG_RANK: Record<string, number> = { alta: 3, media: 2, baja: 1, '': 0 };
const SIN_PROYECTO = '(Sin proyecto)';

export default function BoardOperativa({
  tasks,
  users,
  proyectoStatus,
}: {
  tasks: NormalizedTask[];
  users: UserDoc[];
  proyectoStatus?: Map<string, string>;
}) {
  const [sort, setSort] = useState<SortKey>('deadline');
  const [soloProblema, setSoloProblema] = useState(false);
  const [colapsados, setColapsados] = useState<Set<string>>(new Set());

  const statusDe = (proyecto: string) => proyectoStatus?.get(proyecto) || '';

  const activos = useMemo(() => tasks.filter((t) => !t.excluido), [tasks]);

  // Cantidad de proyectos por estado (finalizado, en proceso, etc.) sobre los proyectos presentes.
  const proyectosPorEstado = useMemo(() => {
    const porProyecto = new Map<string, string>();
    for (const t of tasks) {
      if (t.proyecto) porProyecto.set(t.proyecto, statusDe(t.proyecto));
    }
    const counts: Record<string, number> = {};
    let sinEstado = 0;
    for (const st of porProyecto.values()) {
      if (st) counts[st] = (counts[st] || 0) + 1;
      else sinEstado += 1;
    }
    return { counts, sinEstado, total: porProyecto.size };
  }, [tasks, proyectoStatus]);

  const problema = (t: NormalizedTask) =>
    t.estado_entrega === 'vencido' || t.estado_entrega === 'proximo' || !t.responsable || !t.deadline || t.horas_totales === 0;

  const rows = useMemo(() => {
    let arr = soloProblema ? activos.filter(problema) : activos;
    arr = [...arr].sort((a, b) => {
      switch (sort) {
        case 'deadline': return (a.deadline || '9999').localeCompare(b.deadline || '9999');
        case 'dias_activo': return (b.dias_activo ?? -1) - (a.dias_activo ?? -1);
        case 'responsable': return a.responsable.localeCompare(b.responsable);
        case 'urgencia': return (URG_RANK[b.urgencia] || 0) - (URG_RANK[a.urgencia] || 0);
        case 'estado': return a.estado.localeCompare(b.estado);
      }
    });
    return arr;
  }, [activos, sort, soloProblema]);

  // Agrupamos las filas (ya ordenadas) por proyecto, preservando el orden de aparición.
  const grupos = useMemo(() => {
    const map = new Map<string, NormalizedTask[]>();
    for (const t of rows) {
      const key = t.proyecto || SIN_PROYECTO;
      const arr = map.get(key);
      if (arr) arr.push(t);
      else map.set(key, [t]);
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === SIN_PROYECTO) return 1;
      if (b[0] === SIN_PROYECTO) return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [rows]);

  const toggleGrupo = (name: string) =>
    setColapsados((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  const todoColapsado = grupos.length > 0 && grupos.every(([name]) => colapsados.has(name));
  const toggleTodos = () =>
    setColapsados(todoColapsado ? new Set() : new Set(grupos.map(([name]) => name)));

  const vencidas = activos.filter((t) => t.estado_entrega === 'vencido').length;
  const proximas = activos.filter((t) => t.estado_entrega === 'proximo').length;
  const sinResp = activos.filter((t) => !t.responsable).length;
  const sinDeadline = activos.filter((t) => !t.deadline).length;

  const csvRows = rows.map((t) => ({
    id: t.id, solicitante: t.solicitante, titulo: t.titulo, descripcion: t.descripcion,
    enlace_documentos: t.enlace_documentos, responsable: nameByEmail(t.responsable, users),
    area: t.area_produccion, pieza: t.tipo_tarea, dificultad: t.dificultad, fecha_solicitud: t.fecha_solicitud,
    fecha_inicio: t.fecha_inicio, deadline: t.deadline, entrega: t.fecha_entrega, estado: t.estado,
    estado_entrega: ESTADO_ENTREGA_LABELS[t.estado_entrega], dias_activo: t.dias_activo ?? '',
    dias_para_vencer: t.dias_para_vencer ?? '', horas: t.horas_totales,
    retrabajos: t.retrabajos_count, es_ajuste_cliente: t.es_ajuste ? 'sí' : 'no',
  }));

  const rowStyle = (t: NormalizedTask): React.CSSProperties => {
    if (t.estado_entrega === 'vencido') return { background: 'rgba(185,28,28,0.08)' };
    if (t.estado_entrega === 'proximo') return { background: 'rgba(245,158,11,0.10)' };
    if (!t.responsable || !t.deadline || t.horas_totales === 0) return { background: 'rgba(148,163,184,0.12)' };
    return {};
  };

  // Segmentos (solo estados con al menos un proyecto) para la barra y la leyenda.
  const proyectoSegmentos = [
    ...PROYECTO_STATUSES.map((st) => ({
      key: st as string,
      label: PROYECTO_STATUS_LABELS[st],
      color: PROYECTO_STATUS_COLORS[st],
      count: proyectosPorEstado.counts[st] || 0,
    })),
    { key: 'sin', label: 'Sin estado', color: '#cbd5e1', count: proyectosPorEstado.sinEstado },
  ].filter((s) => s.count > 0);

  return (
    <div className="grid">
      <BoardIntro
        titulo="Tablero operativo del día"
        objetivo="Qué está pasando ahora con cada tarea: qué corre riesgo, qué le falta datos y en qué conviene enfocarse hoy."
        preguntas={['¿Qué tareas están por vencer o vencidas?', '¿Cuáles no tienen responsable o fecha?', '¿Qué priorizar hoy?']}
      />
      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px,1fr))' }}>
        <Kpi label="Backlog activo" value={String(activos.filter((t) => ['pending', 'in_progress', 'paused'].includes(t.estado)).length)} help="Tareas en marcha: pendientes, en progreso o pausadas. Es el trabajo abierto." />
        <Kpi label="Vencidas" value={String(vencidas)} tone={vencidas ? 'bad' : 'ok'} help="Pasaron su fecha límite y todavía no se entregaron." />
        <Kpi label="Próximas a vencer" value={String(proximas)} tone={proximas ? 'warn' : 'ok'} help="Su fecha límite es dentro de los próximos 3 días y siguen sin entregarse." />
        <Kpi label="Sin responsable" value={String(sinResp)} tone={sinResp ? 'warn' : 'ok'} help="Tareas sin nadie asignado: nadie las está siguiendo." />
        <Kpi label="Sin deadline" value={String(sinDeadline)} tone={sinDeadline ? 'warn' : 'ok'} help="Tareas sin fecha límite: no se puede saber si están a tiempo." />
      </div>

      <Card
        title="Proyectos por estado"
        help="Cuántos proyectos distintos aparecen en las tareas visibles, agrupados por el estado del proyecto."
      >
        {proyectosPorEstado.total === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No hay proyectos asociados a las tareas visibles.</p>
        ) : (
          <>
            <p className="proj-summary">
              <strong>{proyectosPorEstado.total}</strong> {proyectosPorEstado.total === 1 ? 'proyecto' : 'proyectos'} en la vista
              {proyectoSegmentos.map((s) => (
                <span key={s.key}> · <strong>{s.count}</strong> {s.label.charAt(0).toLowerCase() + s.label.slice(1)}</span>
              ))}
            </p>
            <div className="proj-bar" role="img" aria-label="Distribución de proyectos por estado">
              {proyectoSegmentos.map((s) => (
                <div key={s.key} className="proj-bar-seg" style={{ flexGrow: s.count, background: s.color }} title={`${s.label}: ${s.count}`} />
              ))}
            </div>
            <div className="proj-legend">
              {proyectoSegmentos.map((s) => (
                <span key={s.key} className="proj-legend-item">
                  <span className="proj-legend-dot" style={{ background: s.color }} />
                  {s.label} <strong>{s.count}</strong>
                </span>
              ))}
            </div>
          </>
        )}
      </Card>

      <Card
        title="Tabla operativa"
        hint="Rojo: vencida · ámbar: próxima a vencer · gris: sin responsable / sin deadline / sin horas."
        action={<ExportButton filename="operativa.csv" rows={csvRows} />}
      >
        <div className="row" style={{ marginBottom: 8, alignItems: 'flex-end' }}>
          <div style={{ minWidth: 180 }}>
            <label>Ordenar por</label>
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="deadline">Deadline</option>
              <option value="dias_activo">Días activo</option>
              <option value="responsable">Responsable</option>
              <option value="urgencia">Urgencia</option>
              <option value="estado">Estado</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={soloProblema} onChange={(e) => setSoloProblema(e.target.checked)} />
            Solo tareas con alerta
          </label>
          <button type="button" className="ghost sm" disabled={!grupos.length} onClick={toggleTodos}>
            {todoColapsado ? 'Expandir todo' : 'Colapsar todo'}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>{rows.length} tareas · {grupos.length} proyectos</span>
        </div>
        <div style={{ overflowX: 'auto', maxHeight: 520 }}>
          <table>
            <thead>
              <tr>
                <th>Solicitante</th><th>Título</th><th>Descripción</th><th>Responsable</th><th>Área</th><th>Dif.</th>
                <th>Deadline</th><th>Entrega</th><th>Estado</th><th className="num">Días act.</th><th className="num">P/vencer</th><th className="num">Horas</th><th className="num" title="Reaperturas (retrabajo interno)">Ret.</th><th>Docs</th>
              </tr>
            </thead>
            {grupos.map(([proyecto, filas]) => {
              const abierto = !colapsados.has(proyecto);
              const st = proyecto === SIN_PROYECTO ? '' : statusDe(proyecto);
              return (
                <tbody key={proyecto}>
                  <tr className="group-header" onClick={() => toggleGrupo(proyecto)}>
                    <td colSpan={14}>
                      <span className="group-caret" aria-hidden>{abierto ? '▾' : '▸'}</span>
                      <strong>{proyecto}</strong>
                      {st && <span className="badge" style={{ background: PROYECTO_STATUS_COLORS[st], color: '#fff', marginLeft: 8 }}>{PROYECTO_STATUS_LABELS[st] || st}</span>}
                      <span className="muted" style={{ marginLeft: 8, fontWeight: 400, fontSize: 12 }}>{filas.length} {filas.length === 1 ? 'tarea' : 'tareas'}</span>
                    </td>
                  </tr>
                  {abierto && filas.map((t) => (
                    <tr key={t.id} style={rowStyle(t)}>
                      <td>{t.solicitante}</td>
                      <td>{t.titulo ? t.titulo.slice(0, 50) : '—'}</td>
                      <td>{t.descripcion.slice(0, 50)}</td>
                      <td>{t.responsable ? nameByEmail(t.responsable, users) : <em>sin asignar</em>}</td>
                      <td>{t.area_produccion || '—'}</td>
                      <td>{t.dificultad || '—'}</td>
                      <td>{t.deadline ? fmtDia(t.deadline) : <em>—</em>}</td>
                      <td>{fmtDate(t.fecha_entrega)}</td>
                      <td><span className="badge" style={{ background: ESTADO_ENTREGA_COLORS[t.estado_entrega], color: '#fff' }}>{ESTADO_ENTREGA_LABELS[t.estado_entrega]}</span></td>
                      <td className="num">{t.dias_activo ?? '—'}</td>
                      <td className="num">{t.dias_para_vencer ?? '—'}</td>
                      <td className="num">{t.horas_totales || '—'}</td>
                      <td className="num">{t.retrabajos_count || ''}</td>
                      <td>{t.enlace_documentos ? <a href={t.enlace_documentos} target="_blank" rel="noopener noreferrer">Ver</a> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              );
            })}
          </table>
        </div>
      </Card>
    </div>
  );
}
